import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play, ArrowRight, BookOpen, TrendingUp, Flame, Sparkles } from "lucide-react";
import { FaSpotify, FaApple, FaYoutube, FaInstagram, FaFacebook } from "react-icons/fa";
import { http } from "@/lib/api";
import SEO from "@/components/SEO";
import Mandala from "@/components/Mandala";
import ReleaseCard from "@/components/ReleaseCard";
import Artwork from "@/components/Artwork";
import { Section, Eyebrow, H2, Reveal } from "@/components/Section";
import { STREAMING } from "@/components/Footer";
import { useMusic } from "@/context/MusicPlayer";
import { TID } from "@/constants/testIds";
import Newsletter from "@/components/Newsletter";

const social = [
  { href: STREAMING.spotify, Icon: FaSpotify, label: "Spotify", tid: TID.socialSpotify },
  { href: STREAMING.apple, Icon: FaApple, label: "Apple Music", tid: TID.socialApple },
  { href: STREAMING.youtube, Icon: FaYoutube, label: "YouTube", tid: TID.socialYoutube },
  { href: STREAMING.instagram, Icon: FaInstagram, label: "Instagram", tid: TID.socialInstagram },
  { href: STREAMING.facebook, Icon: FaFacebook, label: "Facebook", tid: TID.socialFacebook },
];

const CategoryStrip = ({ title, hindi, eyebrow, Icon, category, items }) => {
  if (!items || items.length === 0) return null;
  return (
    <Section className="!py-20">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <Reveal>
          <div className="flex items-center gap-3 text-[#FF9933]">
            <Icon size={18} />
            <span className="text-[10px] uppercase tracking-[0.4em]">{eyebrow}</span>
          </div>
          <div className="mt-4">
            <div className="font-hindi text-2xl md:text-3xl text-white/85">{hindi}</div>
            <h2 className="mt-1 font-display text-4xl md:text-5xl tracking-tight text-white">{title}</h2>
          </div>
        </Reveal>
        <Link
          to={`/releases${category ? `?c=${category}` : ""}`}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#FF9933] hover:text-[#FFD700]"
        >
          Explore <ArrowRight size={14} />
        </Link>
      </div>
      <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {items.slice(0, 4).map((r, i) => <ReleaseCard key={r.id} release={r} index={i} list={items} />)}
      </div>
    </Section>
  );
};

const Home = () => {
  const { play } = useMusic();
  const [featured, setFeatured] = useState([]);
  const [trending, setTrending] = useState([]);
  const [hanuman, setHanuman] = useState([]);
  const [shiva, setShiva] = useState([]);
  const [workout, setWorkout] = useState([]);
  const [meditation, setMeditation] = useState([]);

  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 500], [0, 80]);
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0.4]);
  const mandalaScale = useTransform(scrollY, [0, 500], [1, 1.25]);

  useEffect(() => {
    http.get("/releases", { params: { featured: true } }).then((r) => setFeatured(r.data)).catch(() => {});
    http.get("/releases", { params: { trending: true } }).then((r) => setTrending(r.data)).catch(() => {});
    http.get("/releases", { params: { category: "hanuman" } }).then((r) => setHanuman(r.data)).catch(() => {});
    http.get("/releases", { params: { category: "shiva" } }).then((r) => setShiva(r.data)).catch(() => {});
    http.get("/releases", { params: { category: "workout" } }).then((r) => setWorkout(r.data)).catch(() => {});
    http.get("/releases", { params: { category: "meditation" } }).then((r) => setMeditation(r.data)).catch(() => {});
  }, []);

  const heroFeatured = useMemo(() => featured[0], [featured]);

  const jsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "MusicGroup",
    name: "KASHiX",
    alternateName: "आस्था से अनंत तक",
    description: "Independent Indian artist creating original devotional music — Sanskrit chants, cinematic bhajans, aggressive bhakti.",
    genre: ["Devotional", "Bhajan", "Sanskrit Chant", "Meditation"],
    sameAs: [STREAMING.spotify, STREAMING.apple, STREAMING.youtube, STREAMING.instagram, STREAMING.facebook],
    url: typeof window !== "undefined" ? window.location.origin : "",
  }), []);

  return (
    <>
      <SEO
        title="Where Devotion Becomes Power"
        description="KASHiX — independent Indian devotional music artist. Sanskrit chants, aggressive bhakti, cinematic bhajans."
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-20">
        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="absolute inset-0 hero-radial" />
        <div className="absolute inset-0 grain pointer-events-none" />
        <motion.div style={{ scale: mandalaScale }} className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="spin-slow opacity-50">
            <Mandala size={780} />
          </div>
        </motion.div>
        <div className="absolute left-[18%] top-0 ray" style={{ animationDelay: "-2s" }} />
        <div className="absolute left-1/2 top-0 ray" style={{ animationDelay: "-4s" }} />
        <div className="absolute right-[18%] top-0 ray" style={{ animationDelay: "-6s" }} />

        <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10 py-20 w-full">
          <div className="text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }} className="flex justify-center">
              <Eyebrow>Independent · Devotional · Cinematic</Eyebrow>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 40, letterSpacing: "-0.02em" }}
              animate={{ opacity: 1, y: 0, letterSpacing: "-0.04em" }}
              transition={{ duration: 1.3, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 font-display text-[72px] sm:text-[96px] md:text-[160px] lg:text-[200px] leading-[0.88] tracking-[-0.03em] text-white saffron-text-glow"
            >
              KASHiX
            </motion.h1>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.4 }}
              className="mt-6 font-hindi text-3xl md:text-5xl text-[#FF9933]"
            >
              आस्था से अनंत तक
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 0.6 }}
              className="mt-3 text-[11px] md:text-sm uppercase tracking-[0.45em] text-white/70"
            >
              Experience Where Devotion Becomes Power
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.85 }}
              className="mt-12 flex items-center justify-center flex-wrap gap-4"
            >
              <button
                data-testid={TID.heroCtaExperience}
                onClick={() => heroFeatured && play(heroFeatured)}
                className="group inline-flex items-center gap-3 rounded-full bg-[#FF9933] text-black px-7 py-4 text-xs uppercase tracking-[0.3em] font-semibold hover:bg-[#FFD700] hover:shadow-[0_0_40px_rgba(255,153,51,0.6)] transition-all"
              >
                <Play size={14} fill="currentColor" /> Experience KASHiX
              </button>
              <Link
                data-testid={TID.heroCtaLibrary}
                to="/lyrics"
                className="group inline-flex items-center gap-3 rounded-full border border-white/20 text-white px-7 py-4 text-xs uppercase tracking-[0.3em] font-semibold hover:border-[#FF9933] hover:text-[#FF9933] transition-all"
              >
                <BookOpen size={14} /> Enter the Divine Library
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 1.05 }}
              className="mt-14 flex flex-wrap items-center justify-center gap-6"
            >
              <div className="text-[10px] uppercase tracking-[0.4em] text-white/40">Stream on</div>
              <div className="flex items-center gap-5">
                {social.map(({ href, Icon, label, tid }) => (
                  <a
                    key={label}
                    data-testid={tid}
                    href={href}
                    target="_blank"
                    rel="noreferrer noopener"
                    aria-label={label}
                    className="text-white/70 hover:text-[#FF9933] hover:-translate-y-0.5 transition-all"
                  >
                    <Icon size={20} />
                  </a>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        <div className="absolute bottom-8 left-0 right-0 flex items-center justify-center pointer-events-none">
          <div className="text-[10px] uppercase tracking-[0.4em] text-white/40">Scroll · अनंत की ओर</div>
        </div>
      </section>

      {/* FEATURED RELEASE below hero */}
      {heroFeatured && (
        <Section className="!py-24">
          <Reveal>
            <div className="flex items-center gap-3 text-[#FF9933]">
              <Flame size={18} />
              <span className="text-[10px] uppercase tracking-[0.4em]">Featured Release</span>
            </div>
          </Reveal>
          <div className="mt-8 grid gap-10 lg:grid-cols-12 items-center">
            <Reveal>
              <div className="lg:col-span-5">
                <div className="relative aspect-square overflow-hidden rounded-3xl border border-white/5">
                  <Artwork src={heroFeatured.cover_image} alt={heroFeatured.title} className="absolute inset-0 h-full w-full object-cover" rounded="" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent pointer-events-none" />
                  <div className="absolute inset-0 saffron-glow pointer-events-none rounded-3xl" style={{ boxShadow: "inset 0 0 80px rgba(255,153,51,0.25)" }} />
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="lg:col-span-7">
                <div className="font-hindi text-2xl md:text-3xl text-[#FF9933]">{heroFeatured.hindi_title}</div>
                <h3 className="mt-2 font-display text-4xl md:text-6xl tracking-tight text-white">{heroFeatured.title}</h3>
                <div className="mt-2 text-[11px] uppercase tracking-[0.35em] text-white/60">{heroFeatured.genre} · {heroFeatured.duration}</div>
                <p className="mt-6 text-white/70 leading-relaxed max-w-xl">{heroFeatured.description}</p>
                <div className="mt-8 flex items-center gap-4 flex-wrap">
                  <button
                    onClick={() => play(heroFeatured)}
                    className="inline-flex items-center gap-2 rounded-full bg-[#FF9933] text-black px-6 py-3 text-xs uppercase tracking-[0.3em] font-semibold hover:bg-[#FFD700] hover:shadow-[0_0_30px_rgba(255,153,51,0.6)] transition-all"
                  >
                    <Play size={12} fill="currentColor" /> Play now
                  </button>
                  <Link
                    to={`/releases/${heroFeatured.id}`}
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 text-white px-6 py-3 text-xs uppercase tracking-[0.3em] hover:border-[#FF9933] hover:text-[#FF9933] transition"
                  >
                    Song page <ArrowRight size={14} />
                  </Link>
                </div>
                <div className="mt-8 flex items-center gap-4 text-white/70">
                  <a href={heroFeatured.spotify_url} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-2 hover:text-[#FF9933]">
                    <FaSpotify size={16} /> Spotify
                  </a>
                  <a href={heroFeatured.apple_music_url} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-2 hover:text-[#FF9933]">
                    <FaApple size={16} /> Apple Music
                  </a>
                  <a href={heroFeatured.youtube_url} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-2 hover:text-[#FF9933]">
                    <FaYoutube size={16} /> YouTube
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </Section>
      )}

      {/* Latest Releases */}
      <Section className="!py-20">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <Reveal>
            <Eyebrow>Latest chapters</Eyebrow>
            <div className="mt-4"><H2 hindi="नयी रचनाएँ">The Divine Catalog</H2></div>
          </Reveal>
          <Link to="/releases" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#FF9933] hover:text-[#FFD700]">
            View all releases <ArrowRight size={14} />
          </Link>
        </div>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {featured.slice(0, 4).map((r, i) => <ReleaseCard key={r.id} release={r} index={i} />)}
        </div>
      </Section>

      <CategoryStrip title="Trending Now" hindi="इस समय लोकप्रिय" eyebrow="Trending" Icon={TrendingUp} category="trending" items={trending} />
      <CategoryStrip title="Hanuman Collection" hindi="हनुमान संकलन" eyebrow="Deity Focus" Icon={Flame} category="hanuman" items={hanuman} />
      <CategoryStrip title="Shiva Collection" hindi="शिव संकलन" eyebrow="Mahadev" Icon={Sparkles} category="shiva" items={shiva} />
      <CategoryStrip title="Workout Bhakti" hindi="वर्कआउट भक्ति" eyebrow="Body · Spirit" Icon={Flame} category="workout" items={workout} />
      <CategoryStrip title="Meditation" hindi="ध्यान संगीत" eyebrow="Stillness" Icon={Sparkles} category="meditation" items={meditation} />

      {/* PHILOSOPHY */}
      <Section className="!py-24">
        <div className="grid gap-14 lg:grid-cols-12 items-center">
          <Reveal>
            <div className="lg:col-span-6">
              <Eyebrow>The philosophy</Eyebrow>
              <div className="mt-5"><H2 hindi="डिजिटल मंदिर">A New Category of Devotion</H2></div>
              <p className="mt-8 text-white/70 leading-relaxed">
                KASHiX is not a record label. It is not a temple. It is a new category — a digital
                sanctum where ancient Sanskrit meets cinematic modern production. Every track moves
                the body without breaking devotion, and stills the mind without dulling its edge.
              </p>
              <Link to="/about" className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#FF9933] hover:text-[#FFD700]">
                Read the full story <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>
          <div className="lg:col-span-6">
            <Reveal delay={0.15}>
              <div className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-white/5">
                <img src="https://images.unsplash.com/photo-1768843883385-11ded8a0d016?w=1400&q=80" alt="Temple silhouette" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-black/20 mix-blend-multiply" />
                <div className="absolute inset-0 grain" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="font-hindi text-2xl text-white/90">महाकाल की ध्वनि</div>
                  <div className="mt-1 text-[10px] uppercase tracking-[0.35em] text-[#FF9933]">The Sound of Mahakal</div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </Section>

      {/* Latest Videos strip */}
      <Section className="!py-20">
        <Reveal>
          <Eyebrow>Latest Videos</Eyebrow>
          <div className="mt-4"><H2 hindi="नवीनतम वीडियो">Watch on YouTube</H2></div>
          <p className="mt-4 max-w-2xl text-white/60">Cinematic devotional visuals from the KASHiX YouTube channel.</p>
        </Reveal>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {featured.slice(0, 2).map((r) => (
            <a key={r.id} href={r.youtube_url} target="_blank" rel="noreferrer noopener" className="group relative aspect-video overflow-hidden rounded-2xl border border-white/5 block">
              <Artwork src={r.cover_image} alt={r.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-1000 group-hover:scale-105" rounded="" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-[#FF9933] flex items-center justify-center shadow-[0_0_40px_rgba(255,153,51,0.6)] group-hover:scale-110 transition-transform">
                  <Play size={22} fill="black" className="text-black translate-x-0.5" />
                </div>
              </div>
              <div className="absolute inset-x-0 bottom-0 p-5">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF9933]">YouTube</div>
                <div className="font-display text-xl text-white mt-1">{r.title}</div>
              </div>
            </a>
          ))}
        </div>
      </Section>

      {/* SUPPORT + NEWSLETTER */}
      <Section className="!py-24">
        <div className="grid gap-8 lg:grid-cols-2">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl glass-strong p-10">
              <div className="absolute inset-0 opacity-40" style={{ background: 'radial-gradient(60% 80% at 50% 0%, rgba(255,153,51,0.25), transparent 70%)' }} />
              <div className="relative">
                <Eyebrow>Support the Artist</Eyebrow>
                <div className="mt-4 font-hindi text-2xl text-[#FF9933]">कलाकार का सहयोग</div>
                <h3 className="mt-1 font-display text-3xl md:text-4xl text-white tracking-tight">Fuel the Next Chapter</h3>
                <p className="mt-4 text-white/70">Every stream, share and contribution funds studio time and future releases.</p>
                <Link to="/support" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#FF9933] text-black px-5 py-3 text-xs uppercase tracking-[0.3em] font-semibold hover:bg-[#FFD700] transition">
                  Support KASHiX <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative overflow-hidden rounded-3xl glass-strong p-10">
              <div className="absolute inset-0 opacity-30" style={{ background: 'radial-gradient(60% 80% at 100% 0%, rgba(255,215,0,0.25), transparent 70%)' }} />
              <div className="relative">
                <Eyebrow>Newsletter</Eyebrow>
                <div className="mt-4 font-hindi text-2xl text-[#FF9933]">भीतरी संकुल</div>
                <h3 className="mt-1 font-display text-3xl md:text-4xl text-white tracking-tight">Enter the Inner Circle</h3>
                <p className="mt-4 text-white/70">First access to unreleased chants, meditation drops and licensing catalogs.</p>
                <div className="mt-6"><Newsletter /></div>
              </div>
            </div>
          </Reveal>
        </div>
      </Section>
    </>
  );
};

export default Home;

